/**
 * linetypePatternsFull — the FULL multi-element AutoCAD standard linetype
 * pattern arrays for the vector PDF exporter (PDF-5c.1).
 *
 * # Why this module exists
 *
 * The scene `linetypePatterns` registry (`scene3d/rendering/linetypePatterns.ts`)
 * SUMMARISES every multi-element AutoCAD pattern down to a single (dash, gap)
 * pair — a hard limitation of Three.js `Line2` / `LineMaterial`, which exposes
 * only `dashSize` / `gapSize`. So on-screen CENTER, DASHDOT, DOT, PHANTOM,
 * BORDER, DIVIDE collapse to a plain uniform dash.
 *
 * pdf-lib's `setDashPattern(array, phase)` accepts a FULL multi-element array,
 * so the PDF can render the TRUE pattern — strictly BETTER fidelity than the
 * screen (CLAUDE.md PRIORITY #1 — no simplification). This module supplies the
 * canonical element arrays so the PDF backend can emit the real dash/gap/dot
 * sequence instead of the 2-element summary.
 *
 * # Source of truth + unit reconciliation (CRITICAL)
 *
 * The arrays are DERIVED from {@link STANDARD_LINETYPES} (the single
 * `acad.lin` / `acadiso.lin` registry shared with the SVG renderer and — via
 * `summariseDashPattern` — the Line2 renderer). We do NOT introduce a parallel
 * hand-typed table that could drift: deriving guarantees the BASE UNIT is
 * identical.
 *
 * BYTE-IDENTITY for the simple dash linetypes (DASHED / HIDDEN / their 2/X2
 * variants / the plain 2-element ISO forms): a 2-element `acad.lin` pattern
 * `[d, g]` with NO dot summarises to EXACTLY `{ d, g }` (`summariseDashPattern`
 * picks max-dash / max-gap, which for a 2-element array IS `d` and `g`). The
 * summary path already renders these faithfully, so {@link fullLinetypePattern}
 * returns `null` for them — the caller keeps the existing 2-element
 * `computeEffectiveDash` path, byte-identical by CONSTRUCTION (same code, same
 * float-op order) rather than by floating-point coincidence.
 *
 * A pattern gets the FULL-array path iff the summary is LOSSY for it, i.e.:
 *   - it has > 2 elements (CENTER / DASHDOT / PHANTOM / BORDER / DIVIDE + their
 *     variants, and the multi-element ISO dotted forms — the Line2 summary
 *     collapses these to a single dash/gap), OR
 *   - it contains a DOT element (a sub-0.1 length — e.g. DOT `[0.01, 6.35]`):
 *     `summariseDashPattern` PROMOTES the `0.01` to `0.1` (a tiny dash, not a
 *     dot), so even a 2-element dotted pattern is collapsed and needs the full
 *     path to render a true dot.
 * Those are exactly the linetypes that gain fidelity here.
 *
 * # DXF element semantics (the array this module returns)
 *
 * Each element length is in DRAWING UNITS (mm-equivalents, matching
 * `STANDARD_LINETYPES`), with DXF LTYPE group-49 sign semantics:
 *   - positive  = dash (pen-down) segment of that length,
 *   - negative  = gap  (pen-up)  segment of |length|,
 *   - zero      = dot  (a zero-length pen-down mark; rendered as a round dot by
 *                 the PDF backend's `LineCap.Round`).
 *
 * `STANDARD_LINETYPES` encodes dots as the sentinel `0.01` at EVEN (dash)
 * indices and never uses dots at odd (gap) indices, so the conversion is:
 *   even index, value < 0.1  → 0   (dot)
 *   even index, value ≥ 0.1  → +v  (dash)
 *   odd  index               → −|v| (gap)
 *
 * # Scope (what is NOT here)
 *
 * Only the STANDARD library linetypes get a full pattern. Custom DXF
 * LTYPE-table dash arrays (user-defined entries with group-49 lengths) are
 * still modelled only as the 2-element summary — full custom-array PDF support
 * is tracked for PDF-6 (LTYPE-table persistence). An unknown name returns
 * `null`, signalling the caller to fall back to the 2-element registry path.
 */

import { STANDARD_LINETYPES } from "../../scene3d/rendering/linetypePatterns";

/**
 * Dot sentinel threshold (mm-equivalent). `STANDARD_LINETYPES` encodes a dot as
 * `0.01`; the SVG renderer treats any length `< 0.1` as a dot (rounds to `"0"`
 * in the CSS dasharray). We mirror that exact threshold so a dot stays a dot.
 */
const DOT_THRESHOLD = 0.1;

/** Sentinel names that are SOLID (no pattern) — never get a full array. */
const SOLID_SENTINELS = new Set(["continuous", "bylayer", "byblock"]);

/**
 * Whether a `STANDARD_LINETYPES.dashPattern` is LOSSILY summarised by the scene
 * registry (and so needs the full-array PDF path). True when the pattern has
 * more than 2 elements OR contains a dot (a sub-{@link DOT_THRESHOLD} length —
 * which `summariseDashPattern` promotes to a tiny dash, losing the dot). A
 * plain 2-element dash/gap (DASHED, HIDDEN, ISO02 …) is summarised faithfully,
 * so it returns false and keeps the existing 2-element path (byte-identical).
 */
function needsFullPattern(dashPattern: readonly number[]): boolean {
  if (dashPattern.length > 2) return true;
  return dashPattern.some((v) => v < DOT_THRESHOLD);
}

/**
 * Convert a {@link STANDARD_LINETYPES} `dashPattern` (all-positive mm-equivalent
 * lengths, dots encoded as the `0.01` sentinel) into the DXF-signed element
 * array (positive = dash, negative = gap, 0 = dot).
 *
 * Even indices are dash positions (a value below {@link DOT_THRESHOLD} is a
 * dot → `0`); odd indices are gap positions (→ negative magnitude).
 */
function toSignedElements(dashPattern: readonly number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < dashPattern.length; i++) {
    const v = dashPattern[i] ?? 0;
    if (i % 2 === 0) {
      // Dash position: a sub-threshold length is a DOT (0); otherwise a dash.
      out.push(v < DOT_THRESHOLD ? 0 : v);
    } else {
      // Gap position: pen-up segment, DXF-negative.
      out.push(-Math.abs(v));
    }
  }
  return out;
}

/**
 * Registry: case-folded linetype name → signed element array. Built once at
 * module load from {@link STANDARD_LINETYPES} so it stays in lockstep with the
 * SVG / Line2 renderers' base units. Solid sentinels and empty patterns are
 * EXCLUDED (they have no dash pattern — caller treats them as solid).
 */
const FULL_PATTERN_REGISTRY: ReadonlyMap<string, readonly number[]> = (() => {
  const map = new Map<string, readonly number[]>();
  for (const lt of STANDARD_LINETYPES) {
    const key = lt.name.trim().toLowerCase();
    if (SOLID_SENTINELS.has(key)) continue;
    if (lt.dashPattern.length === 0) continue;
    // Only register patterns the scene registry summarises LOSSILY — a faithful
    // 2-element dash/gap stays on the existing path (byte-identical).
    if (!needsFullPattern(lt.dashPattern)) continue;
    map.set(key, Object.freeze(toSignedElements(lt.dashPattern)));
  }
  return map;
})();

/**
 * Look up the FULL multi-element dash pattern for a STANDARD linetype by
 * case-insensitive name.
 *
 * Returns a fresh `number[]` of signed element lengths in DRAWING UNITS
 * (positive = dash, negative = gap, 0 = dot), or `null` when there is no
 * full-pattern entry for the name — i.e. it is a sentinel / custom / unknown
 * name, OR it is a STANDARD linetype the scene registry summarises FAITHFULLY
 * (a plain 2-element dash/gap such as DASHED / HIDDEN). In every `null` case the
 * caller falls back to the 2-element `computeEffectiveDash` summary path, which
 * is byte-identical for the faithful linetypes.
 *
 * The returned array is a COPY, safe for the caller to scale in place.
 */
export function fullLinetypePattern(name: string | undefined): number[] | null {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  if (!key || SOLID_SENTINELS.has(key)) return null;
  const pattern = FULL_PATTERN_REGISTRY.get(key);
  return pattern ? [...pattern] : null;
}
